import { useEffect, useState } from 'react';

import { useToggle } from '@zazume/zzm-base';

import { useCurrentOrganization } from '#/contexts/AuthProvider';
import { API } from '#lib/network/API';

import { useGet } from '..';
import { OrganizationIntegrationsSettingsKeys } from '../api/organizations/OrganizationIntegrationsSettings.keys';
import { useAsyncMethod } from '../useAsyncMethod';

interface Response {
  isLoading: boolean;
  openZendesk?: () => Promise<void>;
  isEnabled?: boolean;
}

export const useZendesk = (email: string): Response => {
  const { call } = useAsyncMethod();
  const organizationId = useCurrentOrganization();
  const { value: isLoading, toggle: toggleLoading } = useToggle(false);

  const [fetchedUrl, setFetchedUrl] = useState<string | undefined>();

  useEffect(() => setFetchedUrl(undefined), [email]);

  const { data: settings } = useGet(
    OrganizationIntegrationsSettingsKeys.byOrganizationId(organizationId),
    API.organizationIntegrationsSettings.findIntegrationsSettings
  );

  const openZendesk = async () =>
    call(async () => {
      if (isLoading) {
        return;
      }
      if (fetchedUrl) {
        window.open(fetchedUrl, '_blank', 'noreferrer');
        return;
      }
      toggleLoading();
      try {
        const { url } = await API.messaging.getZendeskRedirectURL(email);
        window.open(url, '_blank', 'noreferrer');
        setFetchedUrl(url);
      } finally {
        toggleLoading();
      }
    });

  if (!settings) {
    return {
      isEnabled: false,
      isLoading
    };
  }

  return {
    isEnabled: settings.isZendeskEnabled(),
    openZendesk,
    isLoading
  };
};
